// The Audit log page's pure helpers: action names, actors, targets, detail
// text and the filters kept in the page's URL. Pure: no DOM.
import type { AdminSchemas } from "@10twenty/api/admin";
import { actorLabel } from "./settings";

export type AuditEntry = AdminSchemas["AuditEntry"];

/** Every action the server writes (server/src/admin and the staff trigger), by group. */
export const AUDIT_ACTIONS: Readonly<Record<string, string>> = {
  "auth.enrol": "Enrolled a passkey",
  "auth.sign_in": "Signed in",
  "auth.sign_out": "Signed out",
  "auth.refresh_replayed": "Session ended (refresh token replayed)",
  "staff.grant": "Granted a role",
  "staff.revoke": "Removed a role",
  "staff.enrol": "Created an enrolment link",
  "staff.session_revoke": "Ended an admin session",
  "user.search": "Searched accounts",
  "user.open": "Opened an account",
  "user.suspend": "Suspended an account",
  "user.unsuspend": "Unsuspended an account",
  "user.revoke_sessions": "Signed an account out everywhere",
  "user.avatar_removed": "Removed a profile photo",
  "user.delete": "Deleted an account",
  "circle.search": "Searched circles",
  "circle.open": "Opened a circle",
  "report.list": "Listed reports",
  "report.open": "Opened a report",
  "report.remove_message": "Removed a reported message",
  "report.dismiss": "Dismissed a report",
  "early_access.list": "Listed early access",
  "early_access.export": "Exported early access CSV",
  "early_access.remove": "Removed an early access address",
  "setting.set": "Changed a setting",
};

export const AUDIT_GROUPS: readonly { group: string; label: string }[] = [
  { group: "auth", label: "Sign-in" },
  { group: "staff", label: "Staff" },
  { group: "user", label: "Accounts" },
  { group: "circle", label: "Circles" },
  { group: "report", label: "Moderation" },
  { group: "early_access", label: "Early access" },
  { group: "setting", label: "Settings" },
];

/** Target types the log names, for the filter. */
export const TARGET_TYPES: readonly { type: string; label: string }[] = [
  { type: "user", label: "Account" },
  { type: "circle", label: "Circle" },
  { type: "report", label: "Report" },
  { type: "setting", label: "Setting" },
  { type: "admin_session", label: "Admin session" },
  { type: "early_access", label: "Early access address (hash)" },
];

/** The actions of one group, in `AUDIT_ACTIONS` order. */
export function groupActions(group: string): string[] {
  return Object.keys(AUDIT_ACTIONS).filter((action) => action.startsWith(`${group}.`));
}

export function actionLabel(action: string): string {
  return AUDIT_ACTIONS[action] ?? action;
}

/** Who acted: their address now, "Deleted account", the CLI operator, or the server itself. */
export function auditActorLabel(actor: AuditEntry["actor"]): string {
  return actor.user_id || actor.operator ? actorLabel(actor) : "Server";
}

/** The console page a target opens, if it has one. */
export function targetPath(target: AuditEntry["target"]): string | null {
  if (!target) return null;
  const id = encodeURIComponent(target.id);
  if (target.type === "user") return `/users/${id}`;
  if (target.type === "circle") return `/circles/${id}`;
  if (target.type === "report") return `/moderation/${id}`;
  return null;
}

export function targetLabel(target: AuditEntry["target"]): string {
  if (!target) return "—";
  if (target.label) return target.label;
  if (target.type === "setting") return target.id;
  const type = TARGET_TYPES.find((entry) => entry.type === target.type)?.label ?? target.type;
  return `${type} ${target.id.length > 12 ? `${target.id.slice(0, 8)}…` : target.id}`;
}

function valueText(value: unknown): string {
  if (value === null) return "none";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

/** A row's details as `key: value` pairs, in recorded order; "" when there are none. */
export function detailText(detail: AuditEntry["detail"]): string {
  return Object.entries(detail)
    .map(([key, value]) => `${key.replaceAll("_", " ")}: ${valueText(value)}`)
    .join(" · ");
}

export interface AuditFilters {
  /** A staff member's user id, "none" (CLI or server) or "" (anyone). */
  staff: string;
  /** An exact action, a group or "". */
  action: string;
  target_type: string;
  target_id: string;
}

export const NO_FILTERS: AuditFilters = { staff: "", action: "", target_type: "", target_id: "" };

export function filtersFromParams(params: URLSearchParams): AuditFilters {
  return {
    staff: params.get("staff")?.trim() ?? "",
    action: params.get("action")?.trim() ?? "",
    target_type: params.get("target_type")?.trim() ?? "",
    target_id: params.get("target_id")?.trim() ?? "",
  };
}

/** The non-empty filters, as query parameters (and the page's URL). */
export function filtersQuery(filters: AuditFilters): Record<string, string> {
  const query: Record<string, string> = {};
  for (const key of ["staff", "action", "target_type", "target_id"] as const) {
    const value = filters[key].trim();
    if (value) query[key] = value;
  }
  return query;
}

const ACTION = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Why the server would refuse these filters, if it would. */
export function filtersProblem(filters: AuditFilters): string | null {
  const query = filtersQuery(filters);
  if (query.staff && query.staff !== "none" && !UUID.test(query.staff)) return "Staff must be a user id.";
  if (query.action && !ACTION.test(query.action)) return "That isn't an action name.";
  if (query.target_id && !query.target_type) return "Choose the target's type too.";
  return null;
}
